import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5flow from '@amcharts/amcharts5/flow';
import type { DataItem } from '@amcharts/amcharts5';
import type { ISankeyDataItem } from '@amcharts/amcharts5/flow';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { formatLargeNumber } from '../../utils/number';
import type { SankeyLink, SankeyNode } from './types';

type Props = {
  links: SankeyLink[];
  nodes?: SankeyNode[];
};

const AudienceBuilderSankey = ({ links, nodes = [] }: Props) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const seriesRef = useRef<am5flow.Sankey | null>(null);
  const colorCacheRef = useRef<Map<string, am5.Color>>(new Map());
  const colorContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const nodeNameByIdRef = useRef<Map<string, string>>(new Map());
  const locale = useMemo(
    () => (typeof navigator !== 'undefined' ? navigator.language : undefined),
    [],
  );
  const labelMaxWidth = 140;
  const labelPadding = 24;

  const cleanLabel = (value?: string) => {
    if (!value) return '';
    const normalized = value.replace(/[\r\n]+/g, ' - ').replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    return normalized[0].toUpperCase() + normalized.slice(1);
  };

  const stripTrailingNumber = (value: string) => value.replace(/\s+[\d,.]+[KMB]?$/i, '').trim();
  const hasTrailingNumber = (value: string) => /\s+[\d,.]+[KMB]?$/i.test(value);

  const formatNodeTooltip = (label?: string, value?: number) => {
    const cleaned = cleanLabel(label);
    if (!cleaned) return '';
    if (hasTrailingNumber(cleaned) || value === undefined) return cleaned;
    return `${cleaned} ${formatLargeNumber(value, locale)}`;
  };

  const formatLinkTooltip = (label?: string, value?: number) => {
    if (!label) return '';
    const cleaned = cleanLabel(stripTrailingNumber(label));
    if (!cleaned) return '';
    if (value === undefined) return cleaned;
    return `${cleaned} ${formatLargeNumber(value, locale)}`;
  };

  const isAudienceId = (id?: string) => typeof id === 'string' && id.startsWith('aud-');
  const pickLinkLabel = (
    fromId?: string,
    toId?: string,
    fromName?: string,
    toName?: string,
  ) => {
    if (isAudienceId(fromId)) return fromName ?? fromId;
    if (isAudienceId(toId)) return toName ?? toId;
    return toName ?? fromName ?? toId ?? fromId;
  };

  const getCanvasContext = () => {
    if (colorContextRef.current) return colorContextRef.current;
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    colorContextRef.current = canvas.getContext('2d');
    return colorContextRef.current;
  };

  const normalizeCssColor = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('#') || trimmed.startsWith('rgb')) return trimmed;
    const context = getCanvasContext();
    if (!context) return undefined;
    context.clearRect(0, 0, 1, 1);
    context.fillStyle = trimmed;
    context.fillRect(0, 0, 1, 1);
    const data = context.getImageData(0, 0, 1, 1).data;
    return `rgb(${data[0]}, ${data[1]}, ${data[2]})`;
  };

  const resolveRadixColor = (accent?: string, step?: number) => {
    if (!accent || !step) return undefined;
    const cacheKey = `${accent}-${step}`;
    const cached = colorCacheRef.current.get(cacheKey);
    if (cached) return cached;
    if (typeof document === 'undefined') return undefined;
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue(`--${accent}-${step}`);
    const normalized = normalizeCssColor(cssValue);
    if (!normalized) return undefined;
    try {
      const color = am5.color(normalized);
      colorCacheRef.current.set(cacheKey, color);
      return color;
    } catch {
      return undefined;
    }
  };

  useLayoutEffect(() => {
    if (!chartRef.current) return;
    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const sankey = root.container.children.push(
      am5flow.Sankey.new(root, {
        sourceIdField: 'from',
        targetIdField: 'to',
        valueField: 'value',
        paddingRight: labelMaxWidth + labelPadding,
        paddingLeft: 16,
        nodeWidth: 14,
        nodePadding: 20,
        nodeAlign: 'left',
        minSize: 0.02,
        linkSort: null,
        nodeSort: (a, b) => {
          if (a.depth !== b.depth) return 0;
          const aOrder = (a as any).dataItem?.dataContext?.order;
          const bOrder = (b as any).dataItem?.dataContext?.order;
          if (typeof aOrder === 'number' && typeof bOrder === 'number') {
            return aOrder - bOrder;
          }
          if (typeof aOrder === 'number') return -1;
          if (typeof bOrder === 'number') return 1;
          return 0;
        },
      }),
    );

    sankey.links.template.setAll({
      fillOpacity: 0.45,
      strokeOpacity: 0.12,
      fillStyle: 'solid',
      strokeStyle: 'solid',
    });

    sankey.nodes.setAll({ idField: 'id', nameField: 'name', fillField: 'fill' });
    sankey.nodes.labels.template.setAll({
      oversizedBehavior: 'wrap',
      maxWidth: labelMaxWidth,
      fontSize: 11,
      textAlign: 'left',
      text: '{name}',
    });

    sankey.nodes.template?.setAll({ tooltipText: '' });
    sankey.links.template?.setAll({ tooltipText: '' });

    sankey.nodes.template?.adapters.add('tooltipText', (_text, target) => {
      const dataItem = target.dataItem as DataItem<any> | undefined;
      if (!dataItem) return '';
      const name = dataItem.get('name') as string | undefined;
      const value = (dataItem.get('value') ?? dataItem.get('sum')) as number | undefined;
      return formatNodeTooltip(name, value);
    });

    sankey.links.template?.adapters.add('tooltipText', (_text, target) => {
      const dataItem = target.dataItem as DataItem<any> | undefined;
      if (!dataItem) return '';
      const context = dataItem.dataContext as SankeyLink | undefined;
      const fromId = context?.from;
      const toId = context?.to;
      const nameMap = nodeNameByIdRef.current;
      const fromName = (fromId && nameMap.get(fromId)) || (dataItem.get('fromName') as string | undefined);
      const toName = (toId && nameMap.get(toId)) || (dataItem.get('toName') as string | undefined);
      const label = pickLinkLabel(fromId, toId, fromName, toName);
      const value = dataItem.get('value') as number | undefined;
      return formatLinkTooltip(label, value);
    });

    rootRef.current = root;
    seriesRef.current = sankey;

    return () => {
      root.dispose();
      rootRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!seriesRef.current) return;
    const series = seriesRef.current;
    nodeNameByIdRef.current = new Map(nodes.map((node) => [node.id, node.name]));
    const resolvedNodes = nodes.map((node) => {
      if (!node.color || !node.colorStep) return node;
      const fill = resolveRadixColor(node.color, node.colorStep);
      return fill ? { ...node, fill } : node;
    }) as Array<SankeyNode & { fill?: am5.Color }>;
    const resolvedLinks = links.map((link) => {
      if (!link.color || !link.colorStep) return link;
      const fill = resolveRadixColor(link.color, link.colorStep);
      return fill ? { ...link, fill } : link;
    }) as Array<SankeyLink & { fill?: am5.Color }>;

    if (nodes.length === 0 && links.length > 0) {
      const nodeIds = new Set<string>();
      links.forEach((link) => {
        nodeIds.add(link.from);
        nodeIds.add(link.to);
      });
      series.nodes.data.setAll(Array.from(nodeIds).map((id) => ({ id, name: id })));
    } else {
      series.nodes.data.setAll(resolvedNodes);
    }
    series.data.setAll(resolvedLinks);
    series.dataItems.forEach((dataItem: DataItem<ISankeyDataItem>) => {
      const context = dataItem.dataContext as SankeyLink & { fill?: am5.Color };
      if (!context?.fill) return;
      const link = dataItem.get('link');
      link.setAll({ fill: context.fill, stroke: context.fill });
    });
  }, [links, nodes]);

  return <div ref={chartRef} className="builder-sankey" />;
};

export default AudienceBuilderSankey;
